import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import FocusLock from 'react-focus-lock'
import styled, { css } from 'styled-components'
import { CircularCrossIcon } from 'icons/CircularCross'
import { media } from 'utils/media'
import { useEscapeKey } from 'utils/useEscapeKey'
import { Portal } from 'lib/Portal'
import { Text, textSize } from 'lib/Text'

export type ModalSize = 'auto' | 'fullScreen' | 'large' | 'fit'
export type ModalVariant = 'default' | 'image'

const Container = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1000;
`

interface BoxProps {
  $modalSize: ModalSize
  $showBorder: boolean
  $mobileFullscreen: boolean
}

const TopSection = styled.div<{ variant: ModalVariant }>`
  background-color: white;
  display: flex;
  padding: var(--wide-grid-spacing);
  padding-bottom: 0;
  border-radius: 8px 8px 0 0;

  ${media.phoneDown} {
    border-bottom: 1px solid var(--black-20);
    padding: 16px;
    position: relative;
  }

  ${(props) =>
    props.variant === 'image' &&
    css`
      padding: 0 !important;
    `}
`

const Title = styled(Text)`
  display: inline-flex;
  margin-right: 24px;
  line-height: 40px;
  ${media.phoneDown} {
    ${textSize('m')}
  }
`

const Content = styled.div<{ variant: ModalVariant }>`
  --content-offset-top: 64px; // Approximation of the title size if it's one line.
  padding: var(--wide-grid-spacing);

  ${media.phoneDown} {
    margin-bottom: 8px;
    padding: 16px;
    padding-bottom: 36px; // Add some extra room at the bottom of the container
    overflow: auto;

    // Max height should be the viewport minus the space we give for the nav, minus however
    // much the title section is pushing down the content div.
    height: -webkit-fill-available; // Respect the mobile toolbars
    max-height: calc(100vh - (var(--top-spacing) + var(--content-offset-top)));
  }

  ${(props) =>
    props.variant === 'image' &&
    css`
      margin-bottom: 0 !important;
      padding: 0 !important;
    `}
`

const Box = styled(motion.div)<BoxProps>`
  --top-spacing: 70px; // Space to show the nav
  background-color: white;
  border: 0;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  position: relative;
  margin: 10vh auto;
  max-height: 80vh;
  overflow-y: auto;

  ${(props) =>
    props.$modalSize === 'fit' &&
    css`
      margin: 5vh auto;
      max-height: 90vh;
      width: min-content;
      ${media.phoneDown} {
        width: 100%;
      }
    `}

  ${(props) =>
    props.$modalSize === 'fullScreen' &&
    css`
      max-width: 95vw;
      max-height: 95vh;
      width: 100%;
      height: 100%;
      margin: 2.5vh 2.5vw;
    `}

  ${(props) =>
    props.$modalSize === 'large' &&
    css`
      max-height: 85vh;
      max-width: 85vw;
      width: fit-content;
      padding: 0;
      @media screen and (min-width: 1080px) {
        max-width: 960px;
      }
    `}

  ${(props) =>
    !props.$showBorder &&
    css`
      border: 0;
      box-shadow: 0 0 30px -10px #c7c7c7;
    `}

  @media screen and (max-height: 600px) {
    margin: 12px auto 12px auto;
    max-height: calc(100vh - 24px);
  }

  ${(props) =>
    props.$mobileFullscreen &&
    css`
      ${media.phoneDown} {
        border: none;
        margin: 0;
        position: fixed;
        top: var(--top-spacing);
        left: 8px;
        right: 8px;
        bottom: 0;
        max-height: none;
        max-width: calc(100% - 16px);
        border-radius: 8px 8px 0 0;
        overflow: hidden;
      }
    `}
`

const CloseButton = styled.button<{ withTitle: boolean }>`
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 0;
  position: absolute;
  right: 16px;
  top: 16px;
  ${media.phoneDown} {
    right: 16px;
    top: 20px;
  }
`

const CrossIcon = styled(CircularCrossIcon)`
  width: 20px;

  path {
    fill: var(--black-40);
    transition: fill 0.2s ease-in-out;
  }

  &:hover path {
    fill: black;
  }
`

export interface Props {
  open: boolean
  children: React.ReactNode
  onRequestClose?: () => void
  title?: React.ReactNode
  size?: ModalSize
  variant?: ModalVariant
  showBorder?: boolean
  mobileFullscreen?: boolean
  showClose?: boolean
  className?: string
}

/**
 * @deprecated Use `ModalPopupNew` instead
 */
export const ModalPopup = ({
  open,
  children,
  onRequestClose = () => {},
  title,
  size = 'auto',
  variant = 'default',
  showBorder = true,
  mobileFullscreen = true,
  showClose = true,
  className
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [contentRefVisible, setContentRefVisible] = useState(false)
  useEscapeKey(onRequestClose, open)

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current) {
      e.preventDefault()
      onRequestClose()
    }
  }

  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onRequestClose()
  }

  useEffect(() => {
    const storeOffsetTop = () => {
      if (contentRefVisible) {
        contentRef.current?.style.setProperty('--content-offset-top', `${contentRef.current.offsetTop}px`)
      }
    }

    storeOffsetTop()
    window.addEventListener('resize', storeOffsetTop)

    return () => {
      window.removeEventListener('resize', storeOffsetTop)
    }
  }, [contentRefVisible])

  useEffect(() => {
    if (contentRefVisible && contentRef.current) {
      if (open) {
        disableBodyScroll(contentRef.current)
      } else {
        enableBodyScroll(contentRef.current)
      }
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [open, contentRefVisible])

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          <FocusLock disabled={false}>
            <Container initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleContainerClick} ref={containerRef}>
              <Box
                $showBorder={showBorder}
                $modalSize={size}
                $mobileFullscreen={mobileFullscreen}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}>
                {(title || showClose) && (
                  <TopSection variant={variant}>
                    {title && (
                      <Title weight="medium" size="m">
                        {title}
                      </Title>
                    )}
                    {showClose && (
                      <CloseButton aria-label="Close" onClick={handleCloseClick} withTitle={!!title}>
                        <CrossIcon />
                      </CloseButton>
                    )}
                  </TopSection>
                )}
                <Content
                  variant={variant}
                  className={className}
                  ref={(el) => {
                    contentRef.current = el
                    setContentRefVisible(!!el)
                  }}>
                  {children}
                </Content>
              </Box>
            </Container>
          </FocusLock>
        </Portal>
      )}
    </AnimatePresence>
  )
}
